import Button from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ClockIcon from '@atlaskit/icon/core/clock';
import LocationIcon from '@atlaskit/icon/core/location';
import ShareIcon from '@atlaskit/icon/core/share';
import StoryIcon from '@atlaskit/icon/core/story';
import { Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Heading, Text } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { useNavigate } from 'react-router-dom';

interface Props {
  title: string;
  duration: string;
  location: string;
  isFree: boolean;
  products: string[];
}

export const UpperSection = ({ title, duration, location, isFree, products }: Props) => {
  const navigate = useNavigate();

  const onBackClick = () => navigate(-1);
  const onSaveClick = () => {};
  const onShareClick = () => {};

  return (
    <div css={headerContainerStyles}>
      <Flex gap="space.200" alignItems="center" justifyContent="space-between">
        <Button
          iconBefore={(iconProps) => <ArrowLeftIcon {...iconProps} color={token('color.text.brand')} />}
          appearance="subtle"
          onClick={onBackClick}
        >
          <span css={buttonTextStyles}>Back to class calendar</span>
        </Button>
        <Inline>
          <Button
            iconAfter={(iconProps) => <StoryIcon {...iconProps} color={token('color.text.brand')} />}
            appearance="subtle"
            onClick={onSaveClick}
          >
            <span css={buttonTextStyles}>Save class</span>
          </Button>
          <Button
            iconAfter={(iconProps) => <ShareIcon {...iconProps} color={token('color.text.brand')} />}
            appearance="subtle"
            onClick={onShareClick}
          >
            <span css={buttonTextStyles}>Share</span>
          </Button>
        </Inline>
      </Flex>
      <Heading level={0}>{title}</Heading>
      <div css={metaInfoStyles}>
        <div css={metaInfoItemStyles}>
          <Flex alignItems="start" gap="space.400">
            <Stack space="space.100">
              <Flex alignItems="center" gap="space.050">
                <ClockIcon label="duration" color={token('color.text.brand')} /> <Text size="small">DURATION</Text>
              </Flex>
              <Text size="large">{duration}</Text>
            </Stack>
            <Stack space="space.100">
              <Flex alignItems="center" gap="space.050">
                <LocationIcon label="published" color={token('color.text.brand')} /> <Text size="small">PUBLISHED</Text>
              </Flex>
              <Text size="large">{location}</Text>
            </Stack>
          </Flex>
          <Flex alignItems="start" gap="space.400">
            <Stack space="space.100">
              <Text size="small">APPS TAUGHT</Text>
              <Flex gap="space.050">
                {products.map((product) => (
                  <div key={product} css={productsBeingTaughtStyles}>
                    {product}
                  </div>
                ))}
              </Flex>
            </Stack>
            {isFree && <div css={freeTagStyles}>FREE</div>}
          </Flex>
        </div>
      </div>
    </div>
  );
};

const headerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),
  width: '100%',
});

const buttonTextStyles = css({
  color: token('color.text.brand'),
  textAlign: 'center',
  fontFamily: 'Charlie Text',
  fontSize: '1rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '1.25rem',
});

const metaInfoStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: token('space.400'),

  [media.above.sm]: {
    flexDirection: 'row',
    gap: token('space.400'),
  },
});

const metaInfoItemStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: token('space.400'),

  [media.above.sm]: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: token('space.400'),
  },
});

const freeTagStyles = css({
  marginTop: 'auto',
  padding: `${token('space.075')} ${token('space.100')}`,
  borderRadius: '4px',
  border: `1px solid ${token('color.border.accent.green')}`,
  background: `${token('color.background.accent.green.subtlest')}`,
  color: token('color.text.accent.green'),
  textAlign: 'center',
  fontFamily: 'Charlie Text',
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '1rem',
});

const productsBeingTaughtStyles = css({
  display: 'flex',
  width: 'fit-content',
  padding: `${token('space.075')} ${token('space.100')}`,
  justifyContent: 'center',
  borderRadius: '4px',
  border: `1px solid ${token('color.border')}`,
  background: `${token('color.background.input')}`,
  color: `${token('color.text')}`,
  textAlign: 'center',
  fontFamily: 'Charlie Text',
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '1rem',
});
